import { useApolloClient } from '@apollo/client'
import { InFilterDefinition } from '@factorialco/f0-react'
import {
  EmployeeEmployeesFilterDocument,
  EmployeeEmployeesFilterQuery,
  EmployeesEmployeeNameLabelFragmentDoc,
  EmployeeSelectorV2ItemsDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataSource } from 'modules/core/hooks/useGraphqlDataSource'

const extractConnection = (data: EmployeeEmployeesFilterQuery) =>
  data.employees.employeeNamesConnection

const extractNodes = (connection: ReturnType<typeof extractConnection>) =>
  connection.edges.map((edge) => edge.node)

type Node = NonNullable<ReturnType<typeof extractNodes>>[number]

type Params = {
  label: string
}

export const useEmployeesFilter = ({ label }: Params): InFilterDefinition<string> => {
  const { company } = useCurrentSession()
  const client = useApolloClient()

  const companyId = company.id

  const source = useGraphqlDataSource(
    {
      paginationType: 'infinite-scroll',
      fetchPolicy: 'cache-first',
      document: EmployeeEmployeesFilterDocument,
      perPage: 25,
      mapData: (data) => {
        const connection = extractConnection(data)
        return extractNodes(connection)
      },
      mapPagination: (data) => {
        const connection = extractConnection(data)
        const pageInfo = connection.pageInfo
        const hasNextPage = !!pageInfo.hasNextPage

        const total = connection.totalCount ?? 0

        return {
          total,
          cursor: pageInfo.endCursor ?? '0',
          hasNextPage,
        }
      },
      mapVariables: ({ search }) => ({
        search,
        companyId,
      }),
    },
    [companyId]
  )

  const getLabel = async (value: unknown): Promise<string> => {
    const id = String(value)

    // Try to read from Apollo cache first (no network request)
    try {
      const cacheId = client.cache.identify({
        __typename: 'EmployeesEmployeeName',
        id,
      })

      if (cacheId) {
        const cached = client.readFragment({
          fragment: EmployeesEmployeeNameLabelFragmentDoc,
          fragmentName: 'EmployeesEmployeeNameLabel',
          id: cacheId,
        })

        if (cached?.fullName) {
          return cached.fullName
        }
      }
    } catch {
      // Fragment not found in cache, continue with fallback
    }

    // Fallback: Query the server (with cache-first policy)
    try {
      const { data } = await client.query({
        query: EmployeeSelectorV2ItemsDocument,
        variables: { ids: [id] },
        fetchPolicy: 'cache-first',
      })

      const employee = data?.employees?.employeeNamesConnection?.nodes?.[0]
      if (employee?.fullName) {
        return employee.fullName
      }
    } catch {
      // If query fails, return the ID as string
    }

    return id
  }

  return {
    label,
    type: 'in' as const,
    options: {
      source,
      mapOptions: (employee: Node) => ({
        label: employee.fullName,
        value: employee.id,
      }),
      getLabel,
    },
  }
}
